/**
 * AnimeDetailModal Component
 * Modal for viewing and editing anime details
 */

import { useState, useEffect } from 'react'
import { Save, Trash2, ExternalLink, RefreshCw, Plus } from 'lucide-react'
import Modal from './Modal'
import { DaveScoreChart, DaveScoreSliders } from './DaveScoreChart'
import { getPlatformName } from '../utils/parser'
import { fetchAnimeMetadata } from '../services/jikan'

export function AnimeDetailModal({
  anime,
  isOpen,
  onClose,
  onSave,
  onDelete
}) {
  const [formData, setFormData] = useState({})
  const [activeTab, setActiveTab] = useState('info')
  const [fetchingMetadata, setFetchingMetadata] = useState(false)
  const [imageExpanded, setImageExpanded] = useState(false)

  useEffect(() => {
    if (anime) {
      setFormData({ ...anime })
    }
  }, [anime])

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }))
  }

  const handleScoreChange = (scoreDetail) => {
    const total = Object.values(scoreDetail).reduce((sum, v) => sum + v, 0)
    setFormData(prev => ({
      ...prev,
      daveScoreDetail: scoreDetail,
      daveScore: total
    }))
  }

  const handleSave = () => {
    onSave?.(formData)
    onClose?.()
  }

  const handleDelete = () => {
    if (window.confirm('このアニメを削除しますか？')) {
      onDelete?.(anime.id)
      onClose?.()
    }
  }

  const handleFetchMetadata = async () => {
    if (!formData.title) return

    setFetchingMetadata(true)
    try {
      const metadata = await fetchAnimeMetadata(formData.title)
      if (metadata) {
        setFormData(prev => ({
          ...prev,
          metadata,
          totalEpisodes: metadata.episodes || prev.totalEpisodes
        }))
      }
    } catch (err) {
      console.error('Failed to fetch metadata:', err)
    } finally {
      setFetchingMetadata(false)
    }
  }

  if (!anime) return null

  const imageUrl = formData.metadata?.imageUrl || formData.imageUrl || anime.metadata?.imageUrl || anime.imageUrl
  const season = (formData.season || anime.season || 'シーズン1')?.replace(/^Season\s*/i, 'シーズン')
  const currentEp = formData.currentEpisode ?? anime.currentEpisode ?? 0
  const broadcast = formData.metadata?.broadcast || anime.metadata?.broadcast

  // 放送スケジュールフォーマット
  const formatSchedule = (bc) => {
    if (!bc || !bc.day) return null
    const dayMap = {
      'Sundays': '日曜日', 'Mondays': '月曜日', 'Tuesdays': '火曜日', 'Wednesdays': '水曜日',
      'Thursdays': '木曜日', 'Fridays': '金曜日', 'Saturdays': '土曜日'
    }
    const day = dayMap[bc.day] || bc.day
    let timeStr = ''
    if (bc.time) {
      const [hourStr, minStr] = bc.time.split(':')
      const hour = parseInt(hourStr, 10)
      if (!isNaN(hour)) {
        const isPM = hour >= 12
        const displayHour = hour > 12 ? hour - 12 : hour
        timeStr = `${isPM ? '午後' : '午前'}${displayHour}時`
        if (minStr && minStr !== '00') timeStr += `${minStr}分`
      }
    }
    return `毎週${day} ${timeStr}新着`
  }

  // カスタムヘッダー（カード形式）
  const customHeader = (
    <div className="detail-card-header">
      {imageUrl ? (
        <img
          src={imageUrl}
          alt={formData.title}
          className="detail-card-thumbnail"
          onClick={() => setImageExpanded(true)}
        />
      ) : (
        <div className="detail-card-thumbnail detail-card-thumbnail-placeholder" />
      )}
      <div className="detail-card-info">
        {formData.platform && (
          <div className="detail-card-platform" data-platform={formData.platform}>
            {getPlatformName(formData.platform)}
          </div>
        )}
        <div
          className="detail-card-title"
          onClick={() => {
            if (formData.url) window.open(formData.url, '_blank', 'noopener,noreferrer')
          }}
          style={{ cursor: formData.url ? 'pointer' : 'default' }}
          title={formData.url ? '視聴URLを開く' : ''}
        >
          {formData.title}
        </div>
        <div className="detail-card-episode">{season}：{currentEp}話</div>
        {broadcast && (
          <div className="detail-card-schedule">{formatSchedule(broadcast)}</div>
        )}
      </div>
    </div>
  )

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      customHeader={customHeader}
      footer={
        <>
          <button className="btn btn-danger" onClick={handleDelete}>
            <Trash2 size={16} />
            削除
          </button>
          <button className="btn btn-primary" onClick={handleSave}>
            <Save size={16} />
            保存
          </button>
        </>
      }
    >
      {/* 画像拡大オーバーレイ */}
      {imageExpanded && imageUrl && (
        <div
          className="image-lightbox"
          onClick={() => setImageExpanded(false)}
        >
          <img
            src={imageUrl}
            alt={formData.title}
            className="image-lightbox-img"
          />
        </div>
      )}

      {/* タブ（横並び） */}
      <div className="tabs detail-tabs">
        <button
          className={`tab ${activeTab === 'info' ? 'active' : ''}`}
          onClick={() => setActiveTab('info')}
        >
          基本
        </button>
        <button
          className={`tab ${activeTab === 'score' ? 'active' : ''}`}
          onClick={() => setActiveTab('score')}
        >
          DAVEスコア
        </button>
        <button
          className={`tab ${activeTab === 'metadata' ? 'active' : ''}`}
          onClick={() => setActiveTab('metadata')}
        >
          情報
        </button>
      </div>

      {/* Info Tab */}
      {activeTab === 'info' && (
        <div>

          <div style={{ display: 'flex', gap: '16px' }}>
             <div className="form-group" style={{ flex: 1 }}>
              <label className="form-label">シーズン</label>
              <select
                className="form-input"
                value={formData.season || 'シーズン1'}
                onChange={(e) => handleChange('season', e.target.value)}
              >
                {Array.from({ length: 20 }, (_, i) => `シーズン${i + 1}`).map(val => (
                  <option key={val} value={val}>
                    {val}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div style={{ display: 'flex', gap: '16px' }}>
            <div className="form-group" style={{ flex: 1 }}>
              <label className="form-label">現在のエピソード</label>
              <div style={{ display: 'flex', gap: '8px' }}>
                <input
                  type="number"
                  className="form-input"
                  min="0"
                  value={formData.currentEpisode || 0}
                  onChange={(e) => handleChange('currentEpisode', parseInt(e.target.value, 10) || 0)}
                  style={{ flex: 1 }}
                />
                <button
                  className="btn btn-secondary"
                  onClick={() => handleChange('currentEpisode', (formData.currentEpisode || 0) + 1)}
                  title="+1"
                  style={{ padding: '0 12px' }}
                >
                  <Plus size={16} />
                </button>
              </div>
            </div>
            <div className="form-group" style={{ flex: 1 }}>
              <label className="form-label">総エピソード数</label>
              <input
                type="number"
                className="form-input"
                min="0"
                value={formData.totalEpisodes || ''}
                placeholder="不明"
                onChange={(e) => handleChange('totalEpisodes', parseInt(e.target.value, 10) || null)}
              />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">ステータス</label>
            <select
              className="form-input"
              value={formData.status || 'plan_to_watch'}
              onChange={(e) => handleChange('status', e.target.value)}
            >
              <option value="watching">視聴中</option>
              <option value="plan_to_watch">未視聴</option>
              <option value="completed">完走</option>
              <option value="dropped">ドロップ</option>
              <option value="skipped">スキップ</option>
            </select>
          </div>

          {(formData.status === 'dropped' || formData.status === 'skipped') && (
            <div className="form-group">
              <label className="form-label">理由</label>
              <input
                type="text"
                className="form-input"
                value={formData.dropReason || ''}
                placeholder="例: 1話で脱落、時間がない"
                onChange={(e) => handleChange('dropReason', e.target.value)}
              />
            </div>
          )}

          <div className="form-group">
            <label className="form-label">
              視聴URL
              {formData.url && (
                <a
                  href={formData.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ marginLeft: '8px' }}
                >
                  <ExternalLink size={12} />
                </a>
              )}
            </label>
            <input
              type="url"
              className="form-input"
              value={formData.url || ''}
              onChange={(e) => handleChange('url', e.target.value)}
            />
          </div>

          <div className="form-group">
            <label className="form-label">プラットフォーム</label>
            <select
              className="form-input"
              value={formData.platform || 'other'}
              onChange={(e) => handleChange('platform', e.target.value)}
            >
              <option value="prime">Prime Video</option>
              <option value="netflix">Netflix</option>
              <option value="unext">U-NEXT</option>
              <option value="crunchyroll">Crunchyroll</option>
              <option value="abema">ABEMA</option>
              <option value="other">その他</option>
            </select>
          </div>

          <div className="form-group">
            <label className="form-label">メモ</label>
            <textarea
              className="form-input"
              value={formData.notes || ''}
              placeholder="感想や覚書など"
              rows={3}
              onChange={(e) => handleChange('notes', e.target.value)}
            />
          </div>
        </div>
      )}

      {/* Score Tab */}
      {activeTab === 'score' && (
        <div>
          <DaveScoreChart scoreDetail={formData.daveScoreDetail} />
          <DaveScoreSliders
            scoreDetail={formData.daveScoreDetail || {
              axis_story: 0,
              axis_visual: 0,
              axis_excitement: 0,
              axis_emotion: 0,
              axis_fluency: 0
            }}
            onChange={handleScoreChange}
          />
        </div>
      )}

      {/* Metadata Tab */}
      {activeTab === 'metadata' && (
        <div>
          <button
            className="btn btn-secondary"
            onClick={handleFetchMetadata}
            disabled={fetchingMetadata}
            style={{ marginBottom: '16px' }}
          >
            <RefreshCw size={16} className={fetchingMetadata ? 'animate-spin' : ''} />
            {fetchingMetadata ? 'メタデータ取得中...' : 'Jikan APIから取得'}
          </button>

          <div className="form-group">
            <label className="form-label">画像URL (手動設定)</label>
            <input
              type="url"
              className="form-input"
              value={formData.metadata?.imageUrl || ''}
              placeholder="画像のURLを貼り付け"
              onChange={(e) => {
                const newUrl = e.target.value
                setFormData(prev => ({
                  ...prev,
                  metadata: {
                    ...(prev.metadata || {}),
                    imageUrl: newUrl
                  }
                }))
              }}
            />
          </div>

          {formData.metadata ? (
            <div>
              {formData.metadata.imageUrl && (
                <img
                  src={formData.metadata.imageUrl}
                  alt={formData.title}
                  style={{
                    width: '100px',
                    borderRadius: '8px',
                    marginBottom: '16px'
                  }}
                />
              )}

              <div className="form-group">
                <label className="form-label">スタジオ</label>
                <div style={{ color: 'var(--text-secondary)' }}>
                  {formData.metadata.studios?.join(', ') || '不明'}
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">監督</label>
                <div style={{ color: 'var(--text-secondary)' }}>
                  {formData.metadata.director || '不明'}
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">ジャンル</label>
                <div style={{ color: 'var(--text-secondary)' }}>
                  {formData.metadata.genres?.join(', ') || '不明'}
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">放送状況</label>
                <div style={{ color: 'var(--text-secondary)' }}>
                  {formData.metadata.status || '不明'}
                </div>
              </div>

              {formData.metadata.synopsis && (
                <div className="form-group">
                  <label className="form-label">あらすじ</label>
                  <div style={{
                    color: 'var(--text-secondary)',
                    fontSize: '0.875rem',
                    lineHeight: 1.6
                  }}>
                    {formData.metadata.synopsis.slice(0, 300)}
                    {formData.metadata.synopsis.length > 300 && '...'}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="empty-state">
              <p>メタデータがありません</p>
              <p style={{ fontSize: '0.75rem', marginTop: '8px' }}>
                「Jikan APIから取得」ボタンを押してください
              </p>
            </div>
          )}
        </div>
      )}
    </Modal>
  )
}

export default AnimeDetailModal
